import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, Calendar, DollarSign, Clock, ArrowRight, CheckCircle, XCircle, FileText, Wallet, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const EmployeeDetailPage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [payments, setPayments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");

  const fetchData = useCallback(async () => {
    try {
      const [empRes, payRes, attRes] = await Promise.all([
        api.get(`/employees/${id}`, token),
        api.get(`/employees/${id}/payments`, token),
        api.get(`/attendance?employee_id=${id}`, token)
      ]);
      setEmployee(empRes.data);
      setPayments(payRes.data || []);
      setAttendance(attRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error("خطأ في تحميل بيانات الموظف");
    } finally { setLoading(false); }
  }, [id, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => (n || 0).toLocaleString();
  const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const monthPayments = payments.filter(p => (p.date || p.created_at || "").startsWith(currentMonth));
  const monthPaid = monthPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const monthRemaining = (employee?.salary || 0) - monthPaid;

  // Attendance stats
  const absences = attendance.filter(a => a.type === "absence" || a.type === "غياب");
  const leaves = attendance.filter(a => a.type === "leave" || a.type === "إجازة");
  const lates = attendance.filter(a => a.type === "late" || a.type === "تأخير");

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">جاري التحميل...</div></div>;
  if (!employee) return <div className="text-center py-12 text-gray-500">الموظف غير موجود</div>;

  const tabs = [
    { id: "info", label: "المعلومات", icon: User },
    { id: "salary", label: "الرواتب", icon: DollarSign },
    { id: "attendance", label: "الحضور والغياب", icon: Calendar },
  ];

  return (
    <div className="animate-fade-in" data-testid="employee-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowRight className="w-5 h-5" /></button>
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
          {employee.name?.charAt(0)}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
          <p className="text-gray-500 text-sm">{employee.position || "موظف"} • {employee.phone || ""}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1"><Wallet className="w-4 h-4 text-blue-500" /><span className="text-xs text-gray-500">الراتب الشهري</span></div>
          <p className="text-xl font-bold text-gray-900">{fmt(employee.salary)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-xs text-gray-500">المدفوع هذا الشهر</span></div>
          <p className="text-xl font-bold text-emerald-600">{fmt(monthPaid)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1"><AlertCircle className="w-4 h-4 text-amber-500" /><span className="text-xs text-gray-500">المتبقي</span></div>
          <p className={`text-xl font-bold ${monthRemaining > 0 ? "text-amber-600" : "text-emerald-600"}`}>{fmt(monthRemaining)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-purple-500" /><span className="text-xs text-gray-500">إجمالي المدفوع</span></div>
          <p className="text-xl font-bold text-purple-600">{fmt(totalPaid)}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === tab.id ? "bg-blue-600 text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Info Tab */}
      {activeTab === "info" && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs text-gray-500">الاسم</label>
              <p className="font-bold text-gray-900">{employee.name}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">المنصب</label>
              <p className="font-medium text-gray-700">{employee.position || "-"}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">الهاتف</label>
              <p className="font-medium text-gray-700">{employee.phone || "-"}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">الراتب</label>
              <p className="font-bold text-emerald-600">{fmt(employee.salary)}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">تاريخ التوظيف</label>
              <p className="font-medium text-gray-700">{employee.hire_date || employee.created_at?.split("T")[0] || "-"}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">الحالة</label>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${employee.status === "active" || !employee.status ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                {employee.status === "active" || !employee.status ? "نشط" : "غير نشط"}
              </span>
            </div>
          </div>
          {/* Attendance Summary */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h4 className="font-bold text-gray-800 mb-3">ملخص الحضور</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-red-50 rounded-xl">
                <p className="text-2xl font-bold text-red-600">{absences.length}</p>
                <p className="text-xs text-red-500">غياب</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <p className="text-2xl font-bold text-blue-600">{leaves.length}</p>
                <p className="text-xs text-blue-500">إجازة</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-xl">
                <p className="text-2xl font-bold text-amber-600">{lates.length}</p>
                <p className="text-xs text-amber-500">تأخير</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salary Tab */}
      {activeTab === "salary" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-800">سجل الدفعات ({payments.length})</h3>
          </div>
          {payments.length === 0 ? (
            <div className="text-center py-8 text-gray-400">لا توجد دفعات</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">المبلغ</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">التاريخ</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">الوقت</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={p.id || `pay-${i}`} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4 font-bold text-emerald-600 text-sm">{fmt(p.amount)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{p.date || p.created_at?.split("T")[0] || "-"}</td>
                      <td className="py-3 px-4 text-xs text-gray-400">{fmtDate(p.created_at)}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">{p.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === "attendance" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-800">سجل الحضور والغياب ({attendance.length})</h3>
          </div>
          {attendance.length === 0 ? (
            <div className="text-center py-8 text-gray-400">لا توجد سجلات</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">النوع</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">التاريخ</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">الخصم</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((a, i) => (
                    <tr key={a.id || `att-${i}`} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                          a.type === "absence" || a.type === "غياب" ? "bg-red-100 text-red-700" :
                          a.type === "leave" || a.type === "إجازة" ? "bg-blue-100 text-blue-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {a.type === "absence" ? "غياب" : a.type === "leave" ? "إجازة" : a.type === "late" ? "تأخير" : a.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{a.date || "-"}</td>
                      <td className="py-3 px-4 text-sm text-red-600 font-medium">{a.deduction ? fmt(a.deduction) : "-"}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">{a.notes || a.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeDetailPage;
